import React, { useState, useEffect } from "react";
import { Col } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
    IControlStyles,
    useControlStyles,
} from "../../../assets/layouts";
import { SearchContainer } from "../../../assets/vfx/search/searchContainer";
import Form from "../../../assets/vfx/form/form";
import { Portal } from "../../../assets/vfx/portal/portal";

import { Constants } from "../../../assets/utils/lib/constants";
import { SetState } from "../../../assets/types";
import ReferenceGrid from "./referenceGrid";
import { ReferenceGridDto, SearchReferenceDto } from "../../../services/dto";
import { LookupDto } from "../../../assets/utils/lib/lookupDto";
import { useCommunity } from "../../../context/communityContext";

const defaultSearchQuery: SearchReferenceDto = {
    communityId: Constants.emptyGuid,
    linkTypeId: undefined,
    parentId: undefined,
    isParent: true,
    pageSize: 10,
    pageNumber: 1,
};

interface ReferencePageProps {
    controlStyles?: IControlStyles;
    communities: LookupDto[];
    linkTypeId: string;
    setCommunityId: SetState<string | undefined>;
    onCustomEvent?: (actionType: string, dataId: string) => void;
}

export const ReferencePage = (props: ReferencePageProps) => {
    const { communityId: paramCommunityId } = useParams();
    const { communityId } = useCommunity();
    const resolvedCommunityId: string | undefined = paramCommunityId ?? communityId ?? undefined;
    const [searchQuery, setSearchQuery] = useState<SearchReferenceDto>(defaultSearchQuery);
    const [childQueries, setChildQueries] = useState<Record<string, SearchReferenceDto>>({});
    const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
    const [activeRowData, setActiveRowData] = useState<Record<string, ReferenceGridDto>>({});

    const styles: IControlStyles = props.controlStyles ?? useControlStyles();

    useEffect(() => {
        if (!resolvedCommunityId) return;
        setSearchQuery((prev) =>
            prev.communityId === resolvedCommunityId &&
                prev.linkTypeId === props.linkTypeId
                ? prev
                : {
                    ...prev,
                    communityId: resolvedCommunityId,
                    linkTypeId: props.linkTypeId,
                    pageNumber: 1,
                }
        );

        if (!paramCommunityId) {
            props.setCommunityId(resolvedCommunityId);
        }
    }, [resolvedCommunityId, props.linkTypeId, paramCommunityId, props]);

    const handleSearch = (values: { communityId: string }): void => {
        props.setCommunityId(values.communityId);
        setSearchQuery({
            ...defaultSearchQuery,
            communityId: values.communityId,
            linkTypeId: props.linkTypeId,
        });
    };

    const handleClear = (): void => {
        setSearchQuery((prev) => ({
            ...defaultSearchQuery,
            sortBy: prev.sortBy,
            pageSize: prev.pageSize ?? 10,
        }));
    };

    const getExpandedDescendantIds = (
        activeRows: Record<string, ReferenceGridDto>,
        parentReferenceId: string | undefined
    ): string[] => {
        if (!parentReferenceId) return [];

        const parentIds = new Set<string>([parentReferenceId]);
        const descendantRowIds: string[] = [];
        let foundDescendant = true;

        while (foundDescendant) {
            foundDescendant = false;
            Object.entries(activeRows).forEach(([rowId, row]) => {
                const referenceId = row.referenceID;
                if (
                    referenceId &&
                    row.parentID &&
                    parentIds.has(row.parentID) &&
                    !parentIds.has(referenceId)
                ) {
                    parentIds.add(referenceId);
                    descendantRowIds.push(rowId);
                    foundDescendant = true;
                }
            });
        }

        return descendantRowIds;
    };

    const handleExpandRow = (rowData: ReferenceGridDto, rowId: string): void => {
        const existingRowId = Object.entries(activeRowData).find(
            ([existingId, activeRow]) =>
                existingId !== rowId &&
                expandedRowIds.includes(existingId) &&
                activeRow.referenceID === rowData.referenceID
        )?.[0];
        const rowIdToCollapse = expandedRowIds.includes(rowId)
            ? rowId
            : existingRowId;

        if (rowIdToCollapse) {
            const collapsedReferenceId =
                activeRowData[rowIdToCollapse]?.referenceID ?? rowData.referenceID;
            const descendantIds = getExpandedDescendantIds(
                activeRowData,
                collapsedReferenceId
            );
            const idsToRemove = new Set([rowIdToCollapse, ...descendantIds]);

            setExpandedRowIds((prev) => prev.filter((id) => !idsToRemove.has(id)));
            setActiveRowData((prev) => {
                const next = { ...prev };
                idsToRemove.forEach((id) => delete next[id]);
                return next;
            });
            return;
        }

        setActiveRowData((prev) => ({ ...prev, [rowId]: rowData }));
        setExpandedRowIds((prev) => [...prev, rowId]);
    };

    useEffect(() => {
        setChildQueries({});
    }, [searchQuery.communityId, searchQuery.linkTypeId]);

    const renderNestedComponents = (): (React.JSX.Element | null)[] =>
        expandedRowIds.map((id) => {
            const container = document.getElementById(id);
            if (!container) return null;

            const rowData = activeRowData[id];
            const parentId = rowData?.referenceID;
            if (!parentId) return null;

            const baseChildDefault: SearchReferenceDto = {
                ...defaultSearchQuery,
                isParent: undefined,
                communityId: searchQuery.communityId,
                linkTypeId: searchQuery.linkTypeId,
                parentId,
            };

            const childQuery = childQueries[parentId] ?? baseChildDefault;
            const setChildQueryForThis = (
                update: React.SetStateAction<SearchReferenceDto>
            ) => {
                setChildQueries((prev) => {
                    const prevForId = prev[parentId] ?? baseChildDefault;
                    const nextForId =
                        typeof update === "function"
                            ? (update as (p: SearchReferenceDto) => SearchReferenceDto)(prevForId)
                            : update;
                    return { ...prev, [parentId]: nextForId };
                });
            };

            return (
                <Portal key={id} containerId={id}>
                    <div className="ps-2 pe-3">
                        <ReferenceGrid
                            controlStyles={styles}
                            handleExpandRow={handleExpandRow}
                            isChildGrid
                            linkType={props.linkTypeId}
                            onCustomEvent={props.onCustomEvent}
                            searchQuery={childQuery}
                            setSearchQuery={setChildQueryForThis}
                        />
                    </div>
                </Portal>
            );
        });

    const renderCommunityDropdown = (): React.JSX.Element => (
        <Col lg={4}>
            <Form.Dropdown
                blankOption={{
                    key: Constants.emptyGuid,
                    value: Constants.emptyString,
                    display: "Select Community",
                }}
                options={LookupDto.mapToOptions(props.communities)}
                name="communityId"
                label="Community"
            />
        </Col>
    );

    const renderSearchContainer = (): React.JSX.Element | null => {
        if (paramCommunityId || communityId) {
            return null;
        }

        return (
            <SearchContainer
                initialValues={{ communityId: Constants.emptyGuid }}
                onSearch={handleSearch}
                onClear={handleClear}
                enableReinitialize
            >
                {renderCommunityDropdown()}
            </SearchContainer>
        );
    };

    return (
        <div className="pt-4 px-4">
            <div className="pt-3 px-md-5 px-sm-1">
                <ReferenceGrid
                    controlStyles={styles}
                    searchQuery={searchQuery}
                    onCustomEvent={props.onCustomEvent}
                    handleExpandRow={handleExpandRow}
                    setSearchQuery={setSearchQuery}
                    linkType={props.linkTypeId}
                    filterContent={renderSearchContainer()}
                />
                {renderNestedComponents()}
            </div>
        </div>
    );
};
